/**
 * API 配置文件
 * 统一管理所有 API 端点配置
 */

// 基础配置
export const API_CONFIG = {
  // 使用同源相对路径，避免生产环境跨域（由 Nginx 反向代理到 API 域）
  BASE_URL: '',
  
  // 超时时间
  TIMEOUT: 10000,
  
  // API 版本
  API_VERSION: 'v1'
};

// IAM 服务端点
export const IAM_ENDPOINTS = {
  // 认证相关
  LOGIN: '/iam/tenant-api/v1/users/auth/token',
  REGISTER: '/iam/tenant-api/v1/accounts',  // 使用账户创建端点
  REFRESH_TOKEN: '/iam/tenant-api/v1/users/auth/refresh',
  LOGOUT: '/iam/tenant-api/v1/users/auth/logout',
  VALIDATE_TOKEN: '/iam/tenant-api/v1/users/auth/validate',
  
  // 用户相关
  USER_PROFILE: '/iam/tenant-api/v1/users/user/profile',
  CHANGE_PASSWORD: '/iam/tenant-api/v1/users/user/password',
  USER_PERMISSIONS: '/iam/tenant-api/v1/users/user/permissions',
  
  // 验证相关
  VERIFY_EMAIL: '/iam/tenant-api/v1/users/auth/verify-email',
  SEND_CODE: '/iam/tenant-api/v1/users/auth/send-code',
  RESET_PASSWORD: '/iam/tenant-api/v1/users/auth/reset-password',
  ACCOUNT_CREATION_CODE: '/iam/tenant-api/v1/verification-codes/account-creation',
  PASSWORD_RESET_CODE: '/iam/tenant-api/v1/verification-codes/password-reset',

  // 应用管理相关
  APPS: '/iam/tenant-api/v1/apps',
  APPLICATION_PANEL: '/iam/tenant-api/v1/accounts/{id}/application-panel',
  ACTIVATE_APP: '/iam/tenant-api/v1/accounts/{id}/applications/{applicationCode}',
  SUSPEND_APP: '/iam/tenant-api/v1/accounts/{id}/applications/{applicationCode}/suspend',
  RESUME_APP: '/iam/tenant-api/v1/accounts/{id}/applications/{applicationCode}/resume',
  DEPROVISION_APP: '/iam/tenant-api/v1/accounts/{id}/applications/{applicationCode}',

  // 审计席位管理相关
  SUBSCRIPTION: '/api/v1/accounts/{id}/subscription',
  RENAME_SEAT: '/api/v1/accounts/{id}/seats/{seatId}/rename',
  ASSIGN_SEAT: '/api/v1/accounts/{id}/seats/{seatId}/assign',
  RELEASE_SEAT: '/api/v1/accounts/{id}/seats/{seatId}/release',

  // 账户身份（用户）列表
  ACCOUNT_IDENTITIES: '/api/v1/accounts/{id}/identities',

  // 凭 IAM 令牌换取 Audit 令牌
  AUTH_EXCHANGE: '/api/v1/auth/exchange'
};

/**
 * 公开 API 注册表
 * 列出前端请求路径（带网关前缀）中不需要携带 Authorization 授权头的接口。
 * 同时作为 HTTP 拦截器的 401 白名单。
 *
 * 条目路径与 IAM_ENDPOINTS 中的值保持一致（/iam/tenant-api/v1/... 或 /api/...），
 * 方便一眼对照。匹配时只比较路径部分（忽略协议、域名/IP、端口和 query），
 * 且请求方法必须一致。
 *
 * 分为两类：
 *  - iam项目：后端给出的完整公开 API 列表
 *  - capability项目：项目内额外判定为公开的路径（非 IAM 网关）
 *
 * 注意：GET /iam/tenant-api/v1/permissions/{id}（获取指定权限）带路径参数，
 * 无法用路径全等匹配，由 isPublicApi 里的单独规则处理，不登记在此列表。
 */

const IAM_BACKEND_PUBLIC_API = [
  { method: 'POST', path: '/iam/tenant-api/v1/accounts' },                           // 创建账号
  { method: 'POST', path: '/iam/tenant-api/v1/auth/token' },                         // 登录
  { method: 'POST', path: '/iam/tenant-api/v1/users/auth/token' },                   // 本项目实际登录端点？？？有点奇怪，后端没有这个端点，是不是nginx配置的代理点？？？
  { method: 'GET',  path: '/iam/tenant-api/v1/i18n/countries-or-regions' },           // 国际化-国家/地区
  { method: 'GET',  path: '/iam/tenant-api/v1/i18n/industries' },                     // 国际化-行业
  { method: 'GET',  path: '/iam/tenant-api/v1/i18n/languages' },                      // 国际化-语言
  { method: 'GET',  path: '/iam/tenant-api/v1/registration-types/organizations' },    // Organization 注册类型
  { method: 'GET',  path: '/iam/tenant-api/v1/permissions' },                         // 查找权限
  { method: 'PUT',  path: '/iam/tenant-api/v1/users/password/reset' },                // 重置密码
  { method: 'POST', path: '/iam/tenant-api/v1/verification-codes/account-creation' },// 验证码-创建账号
  { method: 'POST', path: '/iam/tenant-api/v1/verification-codes/user-creation' },    // 验证码-创建用户
  { method: 'POST', path: '/iam/tenant-api/v1/verification-codes/password-reset' }    // 验证码-重置密码
];

const CAPABILITY_BACKEND_PUBLIC_API = [
  { method: 'POST', path: '/api/payment/oss/files' }   // 支付网关 OSS 文件上传（非 IAM 网关）
];

export const PUBLIC_APIS = [
  ...IAM_BACKEND_PUBLIC_API,
  ...CAPABILITY_BACKEND_PUBLIC_API
];

/**
 * 品牌请求头名称
 * 部分 IAM 接口要求携带该请求头，值为 MT 或 LPT（大小写不敏感）。
 */
export const BRAND_HEADER_NAME = 'Ext-Brand';

/**
 * 需要携带品牌请求头（Ext-Brand）的 API 注册表
 * 路径与 IAM_ENDPOINTS / 调用处保持一致，匹配时只比较路径部分，
 * 且请求方法必须一致（忽略大小写）。
 */
const BRAND_HEADER_APIS = [
  { method: 'POST', path: '/iam/tenant-api/v1/accounts' },                            // 创建账号
  { method: 'POST', path: '/iam/tenant-api/v1/users' },                               // 创建用户
  { method: 'POST', path: '/iam/tenant-api/v1/verification-codes/account-creation' }, // 验证码-创建账号
  { method: 'POST', path: '/iam/tenant-api/v1/verification-codes/user-creation' },    // 验证码-创建用户
  { method: 'POST', path: '/iam/tenant-api/v1/verification-codes/password-reset' }    // 验证码-重置密码
];

// 带路径参数的接口：PUT /iam/tenant-api/v1/accounts/{id}/administrator
const ADMINISTRATOR_PATH_PATTERN = /^\/iam\/tenant-api\/v1\/accounts\/[^/]+\/administrator$/;

/**
 * 判断某个请求是否需要携带品牌请求头（Ext-Brand）
 * @param {string} url    请求地址（相对路径或完整 URL，可携带 query）
 * @param {string} [method='GET'] 请求方法
 * @returns {boolean} 需要携带品牌请求头时返回 true
 */
export const needsBrandHeader = (url, method = 'GET') => {
  if (!url) return false;
  const path = getRequestPath(url);
  const reqMethod = String(method || 'GET').toUpperCase();

  // 1. 请求方法一致，且路径与注册表条目完全相同
  if (BRAND_HEADER_APIS.some(api => api.method === reqMethod && api.path === path)) {
    return true;
  }

  // 2. 转移管理员：PUT + 固定前缀/后缀，中间任意一段账户 ID
  if (reqMethod === 'PUT' && ADMINISTRATOR_PATH_PATTERN.test(path)) {
    return true;
  }

  return false;
};

// 带路径参数的公开接口：GET /iam/tenant-api/v1/permissions/{id}
const GET_PERMISSION_PREFIX = '/iam/tenant-api/v1/permissions/';

// 取出路径部分：去掉协议、域名/IP、端口以及 query/hash
const getRequestPath = url => {
  const path = String(url).split(/[?#]/)[0];
  const originMatch = path.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]*(\/.*)$/i);
  return originMatch ? originMatch[1] : path;
};

/**
 * 判断某个请求是否为公开 API（不需要 Authorization 授权头）
 * @param {string} url    请求地址（相对路径或完整 URL，可携带 query）
 * @param {string} [method='GET'] 请求方法
 * @returns {boolean} 命中公开 API 时返回 true
 */
export const isPublicApi = (url, method = 'GET') => {
  if (!url) return false;
  const path = getRequestPath(url);
  const reqMethod = String(method || 'GET').toUpperCase();

  // 1. 请求方法一致，且路径与注册表条目完全相同
  if (PUBLIC_APIS.some(api => api.method === reqMethod && api.path === path)) {
    return true;
  }

  // 2. 获取指定权限：GET + 固定前缀，且前缀之后只剩一段（不含 /）
  if (reqMethod === 'GET' && path.startsWith(GET_PERMISSION_PREFIX)) {
    const id = path.slice(GET_PERMISSION_PREFIX.length);
    return id !== '' && !id.includes('/');
  }

  return false;
};

// 其他服务端点
export const API_ENDPOINTS = {
  // 通用 API
  HEALTH: '/api/health',
  
  // 用户服务（旧版本兼容）
  USERS: '/api/users',
  
  // MCP 服务
  MCP: '/mcp'
};

// 请求头配置
export const HEADERS = {
  JSON: {
    'Content-Type': 'application/json'
  },
  FORM: {
    'Content-Type': 'application/x-www-form-urlencoded'
  },
  MULTIPART: {
    'Content-Type': 'multipart/form-data'
  }
};

// 状态码配置
export const STATUS_CODES = {
  SUCCESS: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500
};

export default {
  API_CONFIG,
  IAM_ENDPOINTS,
  PUBLIC_APIS,
  isPublicApi,
  API_ENDPOINTS,
  HEADERS,
  STATUS_CODES
};